import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  TextField,
  InputAdornment
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search } from '@mui/icons-material';

const DetailsTable = ({ 
  title = "Details",
  data = [],
  columns = [],
  loading = false,
  height = 600,
  searchEnabled = true,
  searchPlaceholder = "Search...",
  onRowClick,
  pageSizeOptions = [10, 25, 50],
  initialPageSize = 10,
  getRowId,
  searchFields = [], // Fields to search in when searchEnabled is true
  ...dataGridProps
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Handle responsive height and initial page size
  const getResponsiveHeight = (height) => {
    if (typeof height === 'object') {
      return height;
    }
    return { xs: Math.min(height, 400), sm: Math.min(height, 500), md: height };
  };

  const getResponsivePageSize = (pageSize) => {
    if (typeof pageSize === 'object') {
      return pageSize.xs || pageSize.sm || pageSize.md || 10;
    }
    return pageSize;
  };

  const responsiveHeight = getResponsiveHeight(height);
  const responsivePageSize = getResponsivePageSize(initialPageSize);

  // Filter data based on search term if search is enabled
  const filteredData = searchEnabled && searchTerm 
    ? data.filter(row => {
        if (searchFields.length > 0) {
          return searchFields.some(field => 
            row[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        // If no specific search fields, search in all string fields
        return Object.keys(row).some(key => {
          const value = row[key];
          if (typeof value === 'string' || typeof value === 'number') {
            return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
          }
          return false;
        });
      })
    : data;

  return (
    <Card sx={{ 
      borderRadius: 1,
      border: '0 solid #fff',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      bgcolor: '#ffffff',
       fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          justifyContent: 'space-between', 
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#1f1f1f',
            letterSpacing: '-0.025em',
            lineHeight: 1,
            fontSize: '1.5rem',
          }}>
            {title}
          </Typography>
          
          {searchEnabled && (
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#737373', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1f1f1f',
                  },
                },
              }}
            />
          )}
        </Box>
        
        <Box sx={{ 
          height: responsiveHeight, 
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          '& .MuiDataGrid-root': {
            borderRadius: 1,
            minWidth: { xs: 600, sm: '100%' }, // Ensure horizontal scroll on mobile
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '1px solid #e2e8f0',
              '& .MuiDataGrid-columnHeader': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                fontWeight: 500,
                color: '#737373',
                padding: { xs: '8px', sm: '16px' },
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f1f5f9',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              color: '#1a202c',
              padding: { xs: '4px 8px', sm: '8px 16px' },
            },
            '& .MuiDataGrid-row': {
              cursor: onRowClick ? 'pointer' : 'default',
              '&:hover': {
                bgcolor: '#f8fafc',
              }
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              '& .MuiTablePagination-toolbar': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: { xs: '8px', sm: '16px' }
            }
          }
        }}>
          <DataGrid
            rows={filteredData}
            columns={columns}
            loading={loading}
            getRowId={getRowId}
            onRowClick={onRowClick}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: responsivePageSize },
              },
            }}
            pageSizeOptions={pageSizeOptions}
            disableRowSelectionOnClick
            {...dataGridProps}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default DetailsTable;